export const DEFAULT_LATE_POLICY = {
  id: 'default',
  name: 'College Standard Late Policy',
  enabled: true,
  requiresApprovedExtension24hBefore: true,
  maxLateDays: 3,
  penaltiesByDay: {
    1: 0.2,
    2: 0.4,
    3: 1.0,
  },
  description:
    'Default late policy: 1 day late = 20% penalty, 2 days late = 40% penalty, 3 or more days late = 100% penalty, unless an extension was approved at least 24 hours before the due date.',
};

const buildDescriptionFromPolicy = (policy) => {
  if (!policy) return DEFAULT_LATE_POLICY.description;

  const entries = Object.entries(policy.penaltiesByDay || {})
    .sort((a, b) => Number(a[0]) - Number(b[0]));

  const segments = entries.map(([day, penalty]) => {
    const numericDay = Number(day);
    const percent = Math.round((penalty || 0) * 100);
    const isTerminalDay = numericDay >= (policy.maxLateDays || numericDay);
    const dayLabel = `${numericDay}${isTerminalDay ? '+' : ''} day${numericDay === 1 ? '' : 's'}`;
    return `${dayLabel} late = ${percent}% penalty`;
  });

  const extensionText = policy.requiresApprovedExtension24hBefore
    ? 'Penalty waived with an approved extension at least 24 hours before the due date.'
    : 'Penalty waived with an approved extension.';

  return `${policy.name || 'Late policy'}: ${segments.join(', ')}. ${extensionText}`;
};

const normalizePenalties = (penaltiesByDay) => {
  const normalized = {};

  if (penaltiesByDay && typeof penaltiesByDay === 'object') {
    Object.entries(penaltiesByDay).forEach(([day, penalty]) => {
      const numericDay = Number(day);
      const numericPenalty = Number(penalty);
      if (Number.isInteger(numericDay) && numericDay > 0 && Number.isFinite(numericPenalty)) {
        const clampedPenalty = Math.min(Math.max(numericPenalty, 0), 1);
        if (clampedPenalty > 0) {
          normalized[numericDay] = clampedPenalty;
        }
      }
    });
  }

  if (Object.keys(normalized).length === 0) {
    return { ...DEFAULT_LATE_POLICY.penaltiesByDay };
  }

  return normalized;
};

export const sanitizeLatePolicy = (policy) => {
  const normalizedPenalties = normalizePenalties(policy?.penaltiesByDay);
  const highestPenaltyDay = Math.max(...Object.keys(normalizedPenalties).map(Number));
  const fallbackMaxLateDays = Number.isInteger(policy?.maxLateDays) && policy.maxLateDays > 0
    ? policy.maxLateDays
    : highestPenaltyDay;

  const safePolicy = {
    ...DEFAULT_LATE_POLICY,
    ...policy,
    penaltiesByDay: normalizedPenalties,
    maxLateDays: fallbackMaxLateDays || DEFAULT_LATE_POLICY.maxLateDays,
    enabled: policy?.enabled ?? DEFAULT_LATE_POLICY.enabled,
    requiresApprovedExtension24hBefore:
      policy?.requiresApprovedExtension24hBefore ?? DEFAULT_LATE_POLICY.requiresApprovedExtension24hBefore,
    id: policy?.id || policy?.courseId || DEFAULT_LATE_POLICY.id,
    name: policy?.name || DEFAULT_LATE_POLICY.name,
  };

  return {
    ...safePolicy,
    description: policy?.description || buildDescriptionFromPolicy(safePolicy),
  };
};

export const describeLatePolicy = (policy) => buildDescriptionFromPolicy(sanitizeLatePolicy(policy));

const DAY_IN_MS = 24 * 60 * 60 * 1000;

export const calculateDaysLate = (submittedAt, dueAt) => {
  if (!submittedAt || !dueAt) return 0;
  const submitted = new Date(submittedAt);
  const due = new Date(dueAt);

  if (Number.isNaN(submitted.getTime()) || Number.isNaN(due.getTime())) return 0;

  const diffMs = submitted.getTime() - due.getTime();
  if (diffMs <= 0) return 0;
  return Math.ceil(diffMs / DAY_IN_MS);
};

export const getPenaltyPercentage = (daysLate, policy = DEFAULT_LATE_POLICY) => {
  const safePolicy = sanitizeLatePolicy(policy);
  if (!safePolicy?.enabled) return 0;
  if (daysLate <= 0) return 0;

  const penalties = safePolicy.penaltiesByDay || {};
  const maxLateDays = safePolicy.maxLateDays || Math.max(...Object.keys(penalties).map(Number), 0);
  const applicableDay = Math.min(daysLate, maxLateDays || daysLate);
  const penalty = penalties[applicableDay] ?? penalties[maxLateDays] ?? 0;
  return Math.min(Math.max(penalty, 0), 1);
};

export const applyLatePolicy = ({
  rawScore,
  submittedAt,
  dueAt,
  hasApprovedExtension = false,
  policy = DEFAULT_LATE_POLICY,
  daysLateOverride,
}) => {
  const safePolicy = sanitizeLatePolicy(policy);
  if (typeof rawScore !== 'number') {
    return { finalScore: 0, penaltyPercentage: 0, daysLate: 0, appliedPolicy: safePolicy };
  }

  if (hasApprovedExtension && safePolicy?.requiresApprovedExtension24hBefore) {
    return { finalScore: Math.max(Math.round(rawScore), 0), penaltyPercentage: 0, daysLate: 0, appliedPolicy: safePolicy };
  }

  const daysLate = typeof daysLateOverride === 'number' && daysLateOverride >= 0
    ? Math.ceil(daysLateOverride)
    : calculateDaysLate(submittedAt, dueAt);
  const penaltyPercentage = getPenaltyPercentage(daysLate, safePolicy);

  const finalScore = Math.max(Math.round(rawScore * (1 - penaltyPercentage)), 0);

  return {
    finalScore,
    penaltyPercentage,
    daysLate,
    appliedPolicy: safePolicy,
  };
};
